import { Table, Dropdown } from "@/config/antd";
import CustomMoreOptions from "@/components/specific/Custom_More_Options";
import { CalendarOutlined } from "@/config/antd";
import "@/assets/styles/custom_table.css";
import PropTypes from "prop-types";
import Column from "antd/es/table/Column";
import Avatar from "@/components/common/Avatar";
import { Link } from "react-router-dom";
import LocationIcon from "@/assets/icons/web_Icons/location.svg";
import { getStatus } from "@/helper/get_status";
import { useState } from "react";
import Delete from "@/components/common/alerts/Delete";
import { useDispatch, useSelector } from "react-redux";
import { selectSearchTerm } from "@/store/redux/slices/search/search_slice";
import { useLoader } from "@/hooks/LoaderProvider";
import {
  selectTownHallLoading,
  deleteTownHall,
} from "@/store/redux/slices/townhall/townhall_slice";
import { selectUserProfile } from "@/store/redux/slices/user/user_slice";

const TownHallTable = ({
  data = [],
  setSelectedMeetings,
  onDelete,
  onModify,
  onTableChange,
}) => {
  const dispatch = useDispatch();
  const { showLoader, hideLoader } = useLoader();
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [selectedTownHall, setSelectedTownHall] = useState(null);
  const isLoading = useSelector(selectTownHallLoading);
  const userProfile = useSelector(selectUserProfile);
  const globalSearchTerm = useSelector(selectSearchTerm) || "";

  const transformedData = data.map((townHall) => ({
    key: townHall?._id || "",
    id: townHall?._id || "",
    _id: townHall?._id || "",
    title: townHall?.title || "",
    publishDate: townHall?.duration?.startDate
      ? new Date(townHall.duration.startDate).toLocaleDateString("en-US", {
          month: "long",
          year: "numeric",
        })
      : "",
    duration: townHall?.duration || {
      startDate: "",
      startTime: "",
      endDate: "",
      endTime: "",
    },
    locations: townHall?.virtualMeetingUrl || "",
    creator: userProfile.fullname,
    creatorImage: userProfile.profileImage,
    type: "Campaign",
    status: townHall?.duration ? getStatus(townHall.duration) : "Unknown",
  }));

  // Apply global search filter
  const filteredData = globalSearchTerm
    ? transformedData.filter(
        (item) =>
          item.title?.toLowerCase().includes(globalSearchTerm.toLowerCase()) ||
          item.creator
            ?.toLowerCase()
            .includes(globalSearchTerm.toLowerCase()) ||
          item.locations
            ?.toLowerCase()
            .includes(globalSearchTerm.toLowerCase()) ||
          item.status?.toLowerCase().includes(globalSearchTerm.toLowerCase())
      )
    : transformedData;

  // rowSelection object indicates the need for row selection
  const rowSelection = {
    onChange: (selectedRowKeys) => {
      setSelectedMeetings(selectedRowKeys);
    },
    getCheckboxProps: (record) => ({
      disabled: record.name === "Disabled User",
      // Column configuration not to be checked
      name: record.name,
    }),
  };

  const handleMenuClick = (menuItem, record) => {
    if (menuItem.key === "delete") {
      setSelectedTownHall(record);
      setDeleteModalVisible(true);
    } else if (menuItem.key === "modify") {
      onModify(record.id);
    }
  };

  const handleDeleteConfirm = async () => {
    if (selectedTownHall) {
      try {
        setDeleteModalVisible(false);
        showLoader();

        // Dispatch the delete action
        await dispatch(deleteTownHall(selectedTownHall._id)).unwrap();
        hideLoader();

        // Call the parent onDelete callback with both id and title
        if (onDelete) {
          onDelete(selectedTownHall._id, selectedTownHall.title);
        }

        // Let the parent component handle success feedback
        if (onTableChange) {
          onTableChange();
        }
      } catch (error) {
        console.error("Failed to delete town hall:", error);
        hideLoader();
      }
    }
  };

  // Only render the table if there is data
  if (data.length === 0) {
    return null;
  }

  return (
    <>
      <Table
        loading={isLoading}
        rowSelection={{
          type: "checkbox",
          ...rowSelection,
        }}
        dataSource={filteredData}
        locale={{
          emptyText: " ",
        }}
        rowKey={(record) => record._id}>
        <>
          <Column
            title="Title and Description"
            dataIndex="title"
            key="title"
            render={(_, { id, title, publishDate }) => (
              <Link
                to={`/dashboard/townhall/${id}`}
                className="flex flex-col justify-start text-left align-start whitespace-nowrap">
                <h3 className="mt-1 font-bold text-bold text-darkGrey">
                  {title || "Untitled"}
                </h3>
                <div className="flex items-center mt-2 text-darkGrey text-small ">
                  <CalendarOutlined className="mr-1" />
                  <span className="text-xs">
                    Published on {publishDate || "N/A"}
                  </span>
                </div>
              </Link>
            )}
          />
          <Column
            title="Duration"
            dataIndex="duration"
            key="duration"
            render={(_, { duration }) => (
              <div className="flex gap-5">
                <div>
                  <h3 className="text-lightGrey text-small">Starting</h3>
                  <p className="text-regular font-bold">
                    {duration?.startDate || "N/A"}
                  </p>
                  <p className="text-regular font-bold">
                    {duration?.startTime || "N/A"}
                  </p>
                </div>
                <div>
                  <h3 className="text-lightGrey text-small">Ending</h3>
                  <p className="text-regular font-bold">
                    {duration?.endDate || "N/A"}
                  </p>
                  <p className="text-regular font-bold">
                    {duration?.endTime || "N/A"}
                  </p>
                </div>
              </div>
            )}
          />
          <Column
            title="Locations"
            dataIndex="locations"
            key="locations"
            render={(_, { id, locations }) => (
              <Link
                to={`/dashboard/townhall/${id}`}
                className="flex items-center gap-2">
                <img src={LocationIcon} alt="" />
                <span className="text-small font-bold">
                  {locations || "No location"}
                </span>
              </Link>
            )}
          />
          <Column
            title="Creator"
            key="creator"
            dataIndex="creator"
            render={(_, { id, creator, creatorImage }) => (
              <Link
                to={`/dashboard/financial/transaction/${id}`}
                className="flex items-center gap-5 text- font-bold">
                <Avatar src={creatorImage} alt={creator || "Creator"} size={40} />
                <p className="text-regular font-bold">
                  {creator || "Not Assigned"}
                </p>
              </Link>
            )}
          />
          <Column
            title="Status"
            key="status"
            dataIndex="status"
            render={(_, { id, status }) => {
              // Determine if the status is expired to apply the right color
              const isExpired = status === "Expired";

              return (
                <Link
                  to={`/dashboard/townhall/${id}`}
                  className="flex items-center gap-1">
                  <div
                    className={`w-2 h-2 rounded-full ${
                      isExpired ? "bg-danger" : "bg-primary"
                    }`}
                  />
                  <p
                    className={`${
                      isExpired ? "text-danger" : "text-primary"
                    } text-small font-bold`}>
                    {status || "Unknown"}
                  </p>
                </Link>
              );
            }}
          />
          <Column
            title=""
            key="action"
            render={(_, record) => (
              <Dropdown
                menu={{
                  items: [
                    {
                      key: "modify",
                      label: "Modify",
                      onClick: (e) => {
                        e.domEvent.stopPropagation();
                        handleMenuClick({ key: "modify" }, record);
                      },
                    },
                    {
                      key: "delete",
                      label: "Delete",
                      onClick: (e) => {
                        e.domEvent.stopPropagation();
                        handleMenuClick({ key: "delete" }, record);
                      },
                    },
                  ],
                }}
                trigger={["click"]}>
                <CustomMoreOptions
                  variant="filled"
                  onClick={(e) => e.stopPropagation()}
                  color="black"
                  orientation="vertical"
                  size="small"
                />
              </Dropdown>
            )}
          />
        </>
      </Table>

      <Delete
        isVisible={deleteModalVisible}
        onClose={() => setDeleteModalVisible(false)}
        onProceed={handleDeleteConfirm}
        header="Delete Town Hall?"
        paragraph={
          selectedTownHall
            ? `Are you sure you want to delete "${
                selectedTownHall.title || "this town hall"
              }"?`
            : ""
        }
        proceedButtonText="Delete"
        cancelButtonText="Cancel"
      />
    </>
  );
};

TownHallTable.propTypes = {
  data: PropTypes.array,
  setSelectedMeetings: PropTypes.func,
  onDelete: PropTypes.func,
  onModify: PropTypes.func,
  onTableChange: PropTypes.func,
};

export default TownHallTable;
